/***
 * File name: exportMixin.js
 *
 * Program Description:
 * This file contains all the reusable functions for exporting as PDF or TSV
 *
 */
import * as html2pdf from 'html2pdf.js'
import JsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

const exportMixin = {
  data: () => ({
    opts: {
      margin: [20, 20, 20, 20],
      pagebreak: { avoid: ['tr', '.export-graph'] },
      image: { type: 'jpeg', quality: 0.8 },
      jsPDF: { unit: 'pt', format: 'a4', orientation: 'landscape' }
    }
  }),
  methods: {
    async exportAsPDF (filename, element) {
      const pdf = await html2pdf().from(element).set(this.opts).toPdf().get('pdf')
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const title = filename === 'BNReport' ? 'Data Breach Notification Report' : 'Annual Security Incident Report'
      const headerPerPage = true
      this.generateHeader(pdf, { pageWidth, pageHeight, title, headerPerPage })
      const pdfFile = pdf.output('bloburl')
      this.downloadFile(pdfFile, filename, 'pdf')
    },
    exportAsTSV (items, filename, headers) {
      if (!items.length) {
        this.$store.dispatch('showAlert', {
          message: 'No data to export.',
          type: 'error'
        })
        return
      }
      // Array of objects to TSV
      const header = headers.map(item => item.text)
      const TSV = [
        header.join('\t'),
        ...items.map(row => headers.map(item => JSON.stringify(row[`${item.value}`])).join('\t'))
      ].join('\r\n')

      window.URL = window.webkitURL || window.URL
      const tsvBlob = new Blob([TSV], { type: 'text/tsv' })
      const tsvFile = window.URL.createObjectURL(tsvBlob)
      this.downloadFile(tsvFile, filename, 'tsv')
    },
    // Draw and Export Data Form in pdf
    drawDataForm (headers, body) {
      const pdf = new JsPDF({
        orientation: 'l',
        unit: 'pt',
        format: 'a4'
      })
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const title = 'Data Breach Notification Report'
      const _columnStyles = {
        dbnNo: { cellWidth: 50 },
        sector: { cellWidth: 50 },
        subsector: { cellWidth: 60 },
        year: { cellWidth: 40 },
        quarter: { cellWidth: 50 },
        month: { cellWidth: 50 },
        classification: { cellWidth: 80 },
        notifType: { cellWidth: 75 },
        status: { cellWidth: 55 },
        docType: { cellWidth: 80 },
        picOrganization: { cellWidth: 40 },
        briefSummary: { cellWidth: 80 },
        withRequest: { cellWidth: 50 },
        typeOfRequest: { cellWidth: 50 }
      }
      const isAdmin = this.$s.userRole.includes('admin')
      const columnStyles = isAdmin ? _columnStyles : {}
      this.generateAutotable(pdf, { headers, body, columnStyles, y: 120 })
      this.generateHeader(pdf, { pageWidth, pageHeight, title })
      const blobUrl = pdf.output('bloburl')
      this.downloadFile(blobUrl, 'BNReport', 'pdf')
    },
    downloadFile (blobUrl, filename, format) {
      window.URL = window.webkitURL || window.URL
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = `${filename}.${format}`
      a.click()
      window.URL.revokeObjectURL(blobUrl)
    },
    generateHeader (pdf, data) {
      const totalPages = [...Array(pdf.internal.getNumberOfPages()).keys()]
      const xAxis = data.pageWidth / 2.2
      const yAxis = data.pageHeight - 10
      for (const _page of totalPages) {
        const page = _page + 1
        pdf.setPage(page)
        pdf.setFontStyle('normal')
        pdf.setFontSize(9)
        pdf.text('dbnms.privacy.gov.ph', xAxis, yAxis)
        if (page === 1 || data.headerPerPage) {
          // Draw line border
          pdf.setDrawColor(18, 31, 107)
          pdf.setLineWidth(3)
          pdf.line(10, 100, data.pageWidth - 10, 100)
          // Set NPC Logo
          const img = '/img/npc-logo.png'
          pdf.addImage(img, 'png', 5, 1, 160, 90)
          // Draw title
          pdf.setFontSize(14)
          pdf.setFontStyle('bold')
          const reportTitleLine = data.title
          const centerXAxis = (data.pageWidth - (pdf.getTextWidth(reportTitleLine))) / 2
          pdf.text(reportTitleLine, centerXAxis, 50)
          // Exported on
          pdf.setFontSize(10)
          pdf.setFontStyle('normal')
          pdf.text('Exported on:', data.pageWidth / 1.12, 40)
          pdf.setFontSize(10)
          pdf.setFontStyle('bold')
          pdf.text(new Date().toLocaleDateString(), data.pageWidth / 1.12, 55)
        }
      }
    },
    generateAutotable (pdf, data) {
      const previousY = (pdf.previousAutoTable && pdf.previousAutoTable.finalY + 10) || 0
      const columns = data.headers.map(item => ({ header: item.text, dataKey: item.value }))
      autoTable(pdf, {
        headStyles: {
          fillColor: '#C0C0C0',
          textColor: 'black'
        },
        styles: {
          minCellWidth: 55,
          overflow: 'linebreak',
          cellPadding: 5
        },
        showHead: 'firstPage',
        columnStyles: data.columnStyles,
        margin: 15,
        startY: data.y || previousY,
        columns,
        body: data.body
      })
    }
  }
}
export default exportMixin
